// The units the hub runs (#1854): the core, and the chapters that come after
// it. A chapter is a unit like the core — the same active-unit storage, its
// own step 1 on resume — and is recorded in `user.tutorial.chapters` once
// finished. A chapter that isn't built yet is hidden, not shown disabled.

import { TUTORIAL_CHAPTER_IDS } from '@/data/userTutorial';
import type { TutorialChapterId, TutorialUnit, UserTutorial } from '@/types';
import { type PrefLedger, parsePrefLedger } from './prefLedger';

/** The chapters this client can run, in hub order; an unbuilt chapter is
 *  hidden. A new chapter is a steps file, its `UNIT_RUNS` entry and its id in
 *  this list (today: `TUTORIAL_CHAPTER_IDS`) — a test checks they agree. */
export const BUILT_CHAPTERS: readonly TutorialChapterId[] = TUTORIAL_CHAPTER_IDS;

const TUTORIAL_UNITS: readonly TutorialUnit[] = ['core', ...BUILT_CHAPTERS];

/** The stored active unit, if it names one this client can run. */
export function parseActiveUnit(value: unknown): TutorialUnit | null {
    return TUTORIAL_UNITS.find(unit => unit === value) ?? null;
}

/** The unit the user is in the middle of, stored with the pref writes it has
 *  to undo — together, so a reload mid-chapter still restores them. */
export interface ActiveUnitRecord {
    unit: TutorialUnit;
    ledger: PrefLedger;
}

/** The stored record: JSON, or a bare unit id as stored before the ledger. */
export function parseActiveRecord(value: string | null): ActiveUnitRecord | null {
    if (value === null) return null;
    const bare = parseActiveUnit(value);
    if (bare !== null) return { unit: bare, ledger: [] };
    try {
        const parsed: unknown = JSON.parse(value);
        if (typeof parsed !== 'object' || parsed === null || !('unit' in parsed)) return null;
        const unit = parseActiveUnit(parsed.unit);
        return unit === null ? null : { unit, ledger: parsePrefLedger('ledger' in parsed ? parsed.ledger : []) };
    } catch {
        return null;
    }
}

/** A unit as the hub shows it. `locked`: a chapter before the core is done.
 *  `new`: never finished (for the core, never left either). */
export type HubStatus = 'new' | 'done' | 'skipped' | 'locked';

export interface HubEntry {
    unit: TutorialUnit;
    status: HubStatus;
}

/** What the user may do in the tutorial project, as far as the chapters care. */
export interface HubAccess {
    /** Notes needs it from its first step: no partial chapter, so no chapter. */
    canAuthorNotes: boolean;
}

/** Whether a stored unit may run again: `false` drops it (the user lost what
 *  it needs — no partial chapter), `null` waits while the access loads. */
export function unitResumable(unit: TutorialUnit, access: HubAccess | null): boolean | null {
    if (unit !== 'notes') return true;
    return access === null ? null : access.canAuthorNotes;
}

export function hubEntries(tutorial: UserTutorial, access: HubAccess): HubEntry[] {
    const coreDone = tutorial.core === 'done';
    return [
        { unit: 'core', status: tutorial.core ?? 'new' },
        ...BUILT_CHAPTERS.filter(unit => unit !== 'notes' || access.canAuthorNotes).map(
            (unit): HubEntry => ({
                unit,
                status: !coreDone ? 'locked' : tutorial.chapters.includes(unit) ? 'done' : 'new',
            }),
        ),
    ];
}
